import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { ArrowUpRight, ArrowDownLeft, Check, X, PlusCircle, Trash2 } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { useLanguage } from '../../context/LanguageContext';

export function UserDetailDialog({
  open, onOpenChange, data, loading,
  addDepositOpen, setAddDepositOpen,
  depositAmount, setDepositAmount,
  depositNote, setDepositNote,
  depositMethod, setDepositMethod,
  onAddDeposit, onConfirmDeposit, onRejectDeposit, onDeleteDeposit,
}) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('admin_transfers_detail')}</DialogTitle>
          {data && <DialogDescription>{data.user.full_name} ({data.user.email})</DialogDescription>}
        </DialogHeader>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">{t('loading')}</div>
        ) : data && (
          <div className="space-y-6 mt-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4" data-testid="user-detail-stats">
              <div className="bg-secondary/30 rounded-xl p-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">{t('balance')}</div>
                <div className="text-2xl font-bold font-mono text-primary" data-testid="detail-balance">¥{(data.stats.balance).toLocaleString()}</div>
              </div>
              <div className="bg-emerald-500/10 rounded-xl p-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">{t('deposit')}</div>
                <div className="text-2xl font-bold font-mono text-emerald-700" data-testid="detail-total-deposited">¥{(data.stats.total_deposited).toLocaleString()}</div>
              </div>
              <div className="bg-blue-500/10 rounded-xl p-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">{t('admin_dash_total_volume')}</div>
                <div className="text-2xl font-bold font-mono text-blue-700" data-testid="detail-total-sent">¥{(data.stats.total_sent).toLocaleString()}</div>
              </div>
            </div>

            {/* User Info */}
            <div className="border border-border/50 rounded-xl p-4">
              <h4 className="font-heading font-semibold text-primary mb-3">{t('profile_basic_info')}</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">{t('phone')}:</span>
                  <span className="ml-2 font-medium">{data.user.phone || '-'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">KYC:</span>
                  <span className="ml-2"><StatusBadge status={data.user.kyc_status} type="kyc" /></span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('admin_users_status')}:</span>
                  <span className="ml-2">
                    {(data.user.account_status || 'active') === 'active' ? t('status_active') :
                     data.user.account_status === 'suspended' ? t('status_suspended') : t('status_frozen')}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('history_date')}:</span>
                  <span className="ml-2 font-medium">{new Date(data.user.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Deposits */}
            <div className="border border-border/50 rounded-xl p-4" data-testid="user-deposit-history">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-heading font-semibold text-primary">{t('deposit')} ({data.stats.deposit_count})</h4>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setAddDepositOpen(true); setDepositAmount(''); setDepositNote(''); }} data-testid="add-deposit-button">
                  <PlusCircle className="h-3.5 w-3.5" />{t('deposit')}
                </Button>
              </div>

              {addDepositOpen && (
                <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 mb-3 space-y-3" data-testid="add-deposit-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('amount')} (JPY)</Label>
                      <Input type="number" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} min="0" data-testid="add-deposit-amount" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('deposit_method')}</Label>
                      <Select value={depositMethod} onValueChange={setDepositMethod}>
                        <SelectTrigger data-testid="add-deposit-method"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bank_transfer">{t('admin_deposits_method_bank')}</SelectItem>
                          <SelectItem value="convenience_store">{t('admin_deposits_method_convenience')}</SelectItem>
                          <SelectItem value="admin_manual">{t('admin_deposits_method_admin')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('admin_kyc_notes')}</Label>
                    <Input value={depositNote} onChange={(e) => setDepositNote(e.target.value)} data-testid="add-deposit-note" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={onAddDeposit} data-testid="submit-deposit-button">{t('deposit')}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddDepositOpen(false)}>{t('cancel')}</Button>
                  </div>
                </div>
              )}

              {data.deposits.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {data.deposits.map((dep) => (
                    <div key={dep.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-secondary/20 text-sm" data-testid={`deposit-item-${dep.id}`}>
                      <div className="flex items-center gap-3">
                        <ArrowDownLeft className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                        <div>
                          <div className="font-mono font-medium">¥{dep.amount.toLocaleString()}</div>
                          <div className="text-xs text-muted-foreground">
                            {dep.method === 'bank_transfer' && t('admin_deposits_method_bank')}
                            {dep.method === 'convenience_store' && t('admin_deposits_method_convenience')}
                            {dep.method === 'admin_manual' && t('admin_deposits_method_admin')}
                            {!dep.method && t('admin_deposits_method_bank')}
                            {' · '}{new Date(dep.created_at).toLocaleString()}
                          </div>
                          {dep.note && <div className="text-xs text-muted-foreground italic mt-0.5">{dep.note}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {dep.status === 'pending' && (
                          <>
                            <StatusBadge status="pending" type="deposit" />
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600 hover:bg-emerald-50" onClick={() => onConfirmDeposit(dep.id)} data-testid={`confirm-deposit-${dep.id}`}>
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => onRejectDeposit(dep.id)} data-testid={`reject-deposit-${dep.id}`}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {dep.status === 'confirmed' && <StatusBadge status="confirmed" type="deposit" />}
                        {dep.status === 'rejected' && <StatusBadge status="rejected" type="deposit" />}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-red-600 hover:bg-red-50" onClick={() => onDeleteDeposit(dep.id)} data-testid={`delete-deposit-${dep.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-4">{t('admin_data_not_found')}</div>
              )}
            </div>

            {/* Transfers */}
            <div className="border border-border/50 rounded-xl p-4" data-testid="user-transfer-history">
              <h4 className="font-heading font-semibold text-primary mb-3">{t('history_title')} ({data.stats.transfer_count})</h4>
              {data.transfers.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {data.transfers.map((tr) => (
                    <div key={tr.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/20 text-sm" data-testid={`transfer-item-${tr.id}`}>
                      <div className="flex items-center gap-3">
                        <ArrowUpRight className="h-4 w-4 text-blue-600" />
                        <div>
                          <div className="font-mono font-medium">¥{tr.total_jpy.toLocaleString()} → R$ {tr.amount_brl.toLocaleString()}</div>
                          <div className="text-xs text-muted-foreground">
                            {tr.recipient_name && <span>{tr.recipient_name} · </span>}
                            {new Date(tr.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <StatusBadge status={tr.status} type="transfer" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-4">{t('admin_data_not_found')}</div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
